import React, { useState, useMemo } from 'react';
import { Box } from '@mantine/core';
import Footer from '../../components/Footer';
import PlatformNavBar from '../../components/PlatformNavBar';
import './AiCalculator.css';

const SESSION_BLOCK_SECONDS = 120

// ─── New pricing model ────────────────────────────────────────────────
const NEW_UNIT_LIST_PRICE = 115
const NEW_SCRIPTED_MINUTES_PER_UNIT = 1600
const NEW_AUTONOMOUS_MINUTES_PER_UNIT = 250

// ─── Cisco Digital pricing model ──────────────────────────────────────
const CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT = 200
const CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT = 4800
const DIGITAL_MESSAGES_PER_STANDARD_SESSION = 10

// ─── Five9 pricing model ──────────────────────────────────────────────
const FIVE9_PRICE_PER_SESSION = 247

// ─── Five9 Digital pricing model ───────────────────────────────────────
const FIVE9_DIGITAL_LIST_PRICE = 115
const FIVE9_DIGITAL_CREDITS_PER_UNIT = 20000

// ─── Google pricing model ─────────────────────────────────────────────
const GOOGLE_FLOWS_PRICE_PER_SECOND = 0.001
const GOOGLE_PLAYBOOKS_PRICE_PER_SECOND = 0.002
const GOOGLE_DIGITAL_PRICE_PER_SESSION = 0.02 // third-party sourced (Google's own pricing page could not be scraped) — flat per text session, any turn count

// ─── Amazon Connect / Lex pricing ─────────────────────────────────────
const AMAZON_LEX_SPEECH_PRICE = 0.004
const AMAZON_CONNECT_BASIC_PRICE_PER_MIN = 0.018
const AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN = 0.038
const AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE = 0.0015

// ─── Genesys pricing model ────────────────────────────────────────────
const GENESYS_PRICE_PER_TOKEN = 1
const GENESYS_SESSIONS_PER_TOKEN = 2
const GENESYS_FREE_TOKENS_NAMED = 250
const GENESYS_FREE_TOKENS_CONCURRENT = 350
const GENESYS_DIGITAL_PRICE_PER_SESSION = 0.02 // 8-turn sessions, flat

// ─── Zoom Contact Center pricing model ─────────────────────────────────
const ZOOM_VOICE_PRICE_PER_MIN = 0.25
const ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT = 0.40

const SOLUTIONS = {
  new_scripted: {
    id: 'new_scripted',
    name: 'Cisco AI Agent',
    subtitle: 'Scripted',
    billingMode: 'minute',
    minutesPerUnit: NEW_SCRIPTED_MINUTES_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${NEW_SCRIPTED_MINUTES_PER_UNIT.toLocaleString()} min/unit · metered to the second`,
    accentColor: '#00bceb',
    isNew: true,
  },
  new_autonomous: {
    id: 'new_autonomous',
    name: 'Cisco AI Agent',
    subtitle: 'Autonomous',
    billingMode: 'minute',
    minutesPerUnit: NEW_AUTONOMOUS_MINUTES_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${NEW_AUTONOMOUS_MINUTES_PER_UNIT.toLocaleString()} min/unit · metered to the second`,
    accentColor: '#6abf4b',
    isNew: true,
  },
  new_split: {
    id: 'new_split',
    name: 'Cisco AI Agent',
    subtitle: 'Scripted + Autonomous (Split)',
    billingMode: 'ciscoSplit',
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `Blended minute split · ${NEW_SCRIPTED_MINUTES_PER_UNIT.toLocaleString()} min/unit Scripted · ${NEW_AUTONOMOUS_MINUTES_PER_UNIT.toLocaleString()} min/unit Autonomous`,
    accentColor: '#7C4DFF',
    isNew: false,
  },
  cisco_digital_scripted: {
    id: 'cisco_digital_scripted',
    name: 'Cisco AI Agent',
    subtitle: 'Scripted Digital',
    billingMode: 'digital',
    sessionsPerUnit: CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit · per 10-message session standard`,
    accentColor: '#00bceb',
    isNew: true,
  },
  cisco_digital_autonomous: {
    id: 'cisco_digital_autonomous',
    name: 'Cisco AI Agent',
    subtitle: 'Autonomous Digital',
    billingMode: 'digital',
    sessionsPerUnit: CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit · per 10-message session standard`,
    accentColor: '#6abf4b',
    isNew: true,
  },
  five9_iva: {
    id: 'five9_iva',
    name: 'Five9',
    subtitle: 'Voice IVA',
    billingMode: 'concurrent',
    unitPrice: FIVE9_PRICE_PER_SESSION,
    description: `$${FIVE9_PRICE_PER_SESSION}/concurrent session · peak calls × price`,
    accentColor: '#e05d44',
    isNew: false,
  },
  five9_digital: {
    id: 'five9_digital',
    name: 'Five9',
    subtitle: 'Digital IVA',
    billingMode: 'messageCredits',
    unitPrice: FIVE9_DIGITAL_LIST_PRICE,
    creditsPerUnit: FIVE9_DIGITAL_CREDITS_PER_UNIT,
    description: `${FIVE9_DIGITAL_CREDITS_PER_UNIT.toLocaleString()} credits/unit · 1 credit per message sent or received`,
    accentColor: '#e05d44',
    isNew: false,
  },
  google_flows: {
    id: 'google_flows',
    name: 'Google',
    subtitle: 'Conversational Agents (Flows)',
    billingMode: 'second',
    unitPrice: GOOGLE_FLOWS_PRICE_PER_SECOND,
    description: `$${GOOGLE_FLOWS_PRICE_PER_SECOND}/sec · total seconds × price`,
    accentColor: '#4285f4',
    isNew: false,
  },
  google_playbooks: {
    id: 'google_playbooks',
    name: 'Google',
    subtitle: 'Conversational Agents (Playbooks)',
    billingMode: 'second',
    unitPrice: GOOGLE_PLAYBOOKS_PRICE_PER_SECOND,
    description: `$${GOOGLE_PLAYBOOKS_PRICE_PER_SECOND}/sec · total seconds × price`,
    accentColor: '#ea4335',
    isNew: false,
  },
  google_digital: {
    id: 'google_digital',
    name: 'Google',
    subtitle: 'Conversational Agents (Digital)',
    billingMode: 'engagement',
    unitPrice: GOOGLE_DIGITAL_PRICE_PER_SESSION,
    description: `$${GOOGLE_DIGITAL_PRICE_PER_SESSION}/text session · flat, regardless of turn count`,
    accentColor: '#34a853',
    isNew: false,
  },
  amazon_connect_basic: {
    id: 'amazon_connect_basic',
    name: 'Amazon Connect',
    subtitle: 'Basic — Lex Voice',
    billingMode: 'lex',
    unitPrice: AMAZON_LEX_SPEECH_PRICE,
    connectPricePerMin: AMAZON_CONNECT_BASIC_PRICE_PER_MIN,
    description: `$${AMAZON_LEX_SPEECH_PRICE}/speech req · +$${AMAZON_CONNECT_BASIC_PRICE_PER_MIN}/min Connect`,
    accentColor: '#FF9900',
    isNew: false,
  },
  amazon_connect_ai_bundled: {
    id: 'amazon_connect_ai_bundled',
    name: 'Amazon Connect',
    subtitle: 'AI Bundled — Lex Voice',
    billingMode: 'lex',
    unitPrice: AMAZON_LEX_SPEECH_PRICE,
    connectPricePerMin: AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN,
    description: `$${AMAZON_LEX_SPEECH_PRICE}/speech req · +$${AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN}/min Connect (AI bundled, no extra AI charges)`,
    accentColor: '#FF9900',
    isNew: false,
  },
  amazon_connect_basic_digital: {
    id: 'amazon_connect_basic_digital',
    name: 'Amazon Connect',
    subtitle: 'Basic — Digital',
    billingMode: 'perMessage',
    unitPrice: AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE,
    description: `$${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE}/message · no bundling`,
    accentColor: '#FF9900',
    isNew: false,
  },
  amazon_connect_ai_bundled_digital: {
    id: 'amazon_connect_ai_bundled_digital',
    name: 'Amazon Connect',
    subtitle: 'AI Bundled — Digital',
    billingMode: 'perMessage',
    unitPrice: AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE,
    description: `$${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE}/message · no bundling`,
    accentColor: '#FF9900',
    isNew: false,
  },
  genesys: {
    id: 'genesys',
    name: 'Genesys',
    subtitle: 'AI Agent',
    billingMode: 'token',
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_SESSIONS_PER_TOKEN} sessions/token · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
  },
  genesys_digital: {
    id: 'genesys_digital',
    name: 'Genesys',
    subtitle: 'Digital',
    billingMode: 'engagement',
    unitPrice: GENESYS_DIGITAL_PRICE_PER_SESSION,
    description: `$${GENESYS_DIGITAL_PRICE_PER_SESSION}/session · 8-turn sessions, flat`,
    accentColor: '#8B2FC9',
    isNew: false,
  },
  zoom_voice: {
    id: 'zoom_voice',
    name: 'Zoom Contact Center',
    subtitle: 'Virtual Agent — Voice',
    billingMode: 'roundedMinute',
    unitPrice: ZOOM_VOICE_PRICE_PER_MIN,
    description: `$${ZOOM_VOICE_PRICE_PER_MIN}/min · rounded up to the minute`,
    accentColor: '#0B5CFF',
    isNew: false,
  },
  zoom_digital: {
    id: 'zoom_digital',
    name: 'Zoom Contact Center',
    subtitle: 'Virtual Agent — Digital',
    billingMode: 'engagement',
    unitPrice: ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT,
    description: `$${ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT}/engagement`,
    accentColor: '#0B5CFF',
    isNew: false,
  },
}

const SOLUTION_ORDER = [
  'new_scripted', 'new_autonomous', 'new_split',
  'cisco_digital_scripted', 'cisco_digital_autonomous',
  'five9_iva', 'five9_digital',
  'google_flows', 'google_playbooks', 'google_digital',
  'amazon_connect_basic', 'amazon_connect_ai_bundled',
  'amazon_connect_basic_digital', 'amazon_connect_ai_bundled_digital',
  'genesys', 'genesys_digital',
  'zoom_voice', 'zoom_digital',
]

function calculateResults({ numCalls, avgCallLength, peakConcurrent, numSpeechRequests, licenseType, solution, overridePrice, overrideConnectPrice, numDigitalInteractions = 0, avgMessagesPerSession = 10, splitTotalSeconds = 0, splitScriptedPct = 0 }) {
  if (solution.billingMode === 'lex') {
    const effectiveSpeechPrice = overridePrice > 0 ? overridePrice : AMAZON_LEX_SPEECH_PRICE
    const effectiveConnectPrice = overrideConnectPrice > 0 ? overrideConnectPrice : solution.connectPricePerMin
    const lexSpeechCost = numSpeechRequests * numCalls * effectiveSpeechPrice
    const connectTotalCost = numCalls * (avgCallLength / 60) * effectiveConnectPrice
    return {
      billingMode: 'lex',
      numSpeechRequests,
      effectiveSpeechPrice,
      effectiveConnectPrice,
      lexSpeechCost,
      connectTotalCost,
      totalCostWithoutConnect: lexSpeechCost,
      totalCostWithConnect: lexSpeechCost + connectTotalCost,
      numCalls,
      avgCallLengthSecs: avgCallLength,
    }
  }

  if (solution.billingMode === 'digital') {
    const normalizedSessions = numDigitalInteractions * (avgMessagesPerSession / DIGITAL_MESSAGES_PER_STANDARD_SESSION)
    const listPrice = solution.listPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const unitsRequired = Math.ceil(normalizedSessions / solution.sessionsPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'digital', normalizedSessions, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions, avgMsgs: avgMessagesPerSession }
  }

  if (solution.billingMode === 'engagement') {
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const totalCost = numDigitalInteractions * effectiveUnitPrice
    return { billingMode: 'engagement', totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions }
  }

  if (solution.billingMode === 'messageCredits') {
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const totalMessages = numDigitalInteractions * avgMessagesPerSession
    const unitsRequired = Math.ceil(totalMessages / solution.creditsPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'messageCredits', totalMessages, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions, avgMsgs: avgMessagesPerSession }
  }

  if (solution.billingMode === 'perMessage') {
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const totalMessages = numDigitalInteractions * avgMessagesPerSession
    const totalCost = totalMessages * effectiveUnitPrice
    return { billingMode: 'perMessage', totalMessages, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions, avgMsgs: avgMessagesPerSession }
  }

  if (solution.billingMode === 'ciscoSplit') {
    const pct = Math.min(100, Math.max(0, splitScriptedPct))
    const listPrice = solution.listPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const splitTotalMinutes = splitTotalSeconds / 60
    const scriptedMinutes = splitTotalMinutes * (pct / 100)
    const autonomousMinutes = splitTotalMinutes * (1 - pct / 100)
    const scriptedUnits = Math.ceil(scriptedMinutes / NEW_SCRIPTED_MINUTES_PER_UNIT)
    const autonomousUnits = Math.ceil(autonomousMinutes / NEW_AUTONOMOUS_MINUTES_PER_UNIT)
    const unitsRequired = scriptedUnits + autonomousUnits
    const scriptedCost = scriptedUnits * effectiveUnitPrice
    const autonomousCost = autonomousUnits * effectiveUnitPrice
    return {
      billingMode: 'ciscoSplit', splitTotalSeconds, splitTotalMinutes, splitScriptedPct: pct,
      scriptedMinutes, autonomousMinutes, scriptedUnits, autonomousUnits,
      scriptedCost, autonomousCost, unitsRequired,
      totalCost: scriptedCost + autonomousCost,
      effectiveUnitPrice, listPrice,
    }
  }

  const listPrice = solution.isNew ? solution.listPrice : solution.unitPrice
  const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice

  if (solution.billingMode === 'session') {
    const sessionsPerCall = Math.ceil(avgCallLength / SESSION_BLOCK_SECONDS)
    const totalSessions = numCalls * sessionsPerCall
    const unitsRequired = Math.ceil(totalSessions / solution.sessionsPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'session', sessionsPerCall, totalSessions, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'minute') {
    const totalMinutes = numCalls * (avgCallLength / 60)
    const unitsRequired = Math.ceil(totalMinutes / solution.minutesPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'minute', totalMinutes, avgCallLengthSecs: avgCallLength, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'roundedMinute') {
    const minutesPerCall = Math.ceil(avgCallLength / 60)
    const totalMinutes = numCalls * minutesPerCall
    const totalCost = totalMinutes * effectiveUnitPrice
    return { billingMode: 'roundedMinute', minutesPerCall, totalMinutes, avgCallLengthSecs: avgCallLength, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'concurrent') {
    const totalCost = peakConcurrent * effectiveUnitPrice
    return { billingMode: 'concurrent', peakConcurrent, totalCost, effectiveUnitPrice, listPrice }
  } else if (solution.billingMode === 'token') {
    const sessionsPerCall = Math.ceil(avgCallLength / SESSION_BLOCK_SECONDS)
    const totalSessions = numCalls * sessionsPerCall
    const tokensRequired = Math.ceil(totalSessions / GENESYS_SESSIONS_PER_TOKEN)
    const freeTokens = licenseType === 'concurrent' ? GENESYS_FREE_TOKENS_CONCURRENT : GENESYS_FREE_TOKENS_NAMED
    const billableTokens = Math.max(0, tokensRequired - freeTokens)
    const totalCost = billableTokens * effectiveUnitPrice
    return { billingMode: 'token', sessionsPerCall, totalSessions, tokensRequired, freeTokens, billableTokens, totalCost, effectiveUnitPrice, listPrice, numCalls, licenseType }
  } else {
    const totalSeconds = numCalls * avgCallLength
    const totalCost = totalSeconds * effectiveUnitPrice
    return { billingMode: 'second', totalSeconds, avgCallLengthSecs: avgCallLength, totalCost, effectiveUnitPrice, listPrice, numCalls }
  }
}

function aggregateScenarioResults(scenarioResults, billingMode) {
  const first = scenarioResults[0]
  const sum = (fn) => scenarioResults.reduce((acc, r) => acc + fn(r), 0)
  if (billingMode === 'lex') {
    return {
      billingMode: 'lex',
      lexSpeechCost: sum(r => r.lexSpeechCost),
      connectTotalCost: sum(r => r.connectTotalCost),
      totalCostWithoutConnect: sum(r => r.totalCostWithoutConnect),
      totalCostWithConnect: sum(r => r.totalCostWithConnect),
      numCalls: sum(r => r.numCalls),
      effectiveSpeechPrice: first.effectiveSpeechPrice,
      effectiveConnectPrice: first.effectiveConnectPrice,
      numSpeechRequests: null,
    }
  }
  return {
    ...first,
    numCalls: sum(r => r.numCalls),
    totalCost: sum(r => r.totalCost),
    totalMinutes: sum(r => r.totalMinutes ?? 0),
    totalSeconds: sum(r => r.totalSeconds ?? 0),
    totalSessions: sum(r => r.totalSessions ?? 0),
    unitsRequired: sum(r => r.unitsRequired ?? 0),
    tokensRequired: sum(r => r.tokensRequired ?? 0),
    billableTokens: sum(r => r.billableTokens ?? 0),
  }
}

// ─── Sub-components ───────────────────────────────────────────────────

function InputField({ label, hint, value, onChange, min = 0, max, step = 1, prefix, suffix, placeholder }) {
  return (
    <div className="input-field">
      <label>{label}</label>
      {hint && <span className="input-hint">{hint}</span>}
      <div className="input-wrapper">
        {prefix && <span className="input-adornment prefix">{prefix}</span>}
        <input
          type="number"
          value={value}
          min={min}
          {...(max !== undefined ? { max } : {})}
          step={step}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span className="input-adornment suffix">{suffix}</span>}
      </div>
    </div>
  )
}

function BreakdownRow({ label, value }) {
  return (
    <div className="breakdown-row">
      <span className="breakdown-label">{label}</span>
      <span className="breakdown-value">{value}</span>
    </div>
  )
}

function ResultCard({ solution, results, isReady, numScenarios }) {
  const { billingMode } = results
  const multi = numScenarios > 1
  const unitsRequired = results.unitsRequired
  const totalCost = results.totalCost
  const effectiveUnitPrice = results.effectiveUnitPrice
  const listPrice = results.listPrice
  const usingCustomPrice = effectiveUnitPrice !== listPrice

  return (
    <div className="result-card" style={{ '--accent': solution.accentColor }}>
      <div className="result-card-header" style={{ borderTopColor: solution.accentColor }}>
        <div className="card-header-top">
          <div className="solution-name">{solution.name}</div>
        </div>
        <div className="solution-subtitle" style={{ color: solution.accentColor }}>
          {solution.subtitle}
        </div>
        <div className="solution-desc">{solution.description}</div>
      </div>

      <div className="result-card-body">
        {isReady ? (
          <>
            <div className="breakdown">
              {billingMode === 'session' && (
                <>
                  {!multi && <BreakdownRow label="Sessions per call" value={results.sessionsPerCall.toLocaleString()} />}
                  <BreakdownRow label="Total sessions" value={results.totalSessions.toLocaleString()} />
                  <BreakdownRow label="AI Agent Units required" value={unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={<span>${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  />
                </>
              )}
              {billingMode === 'minute' && (
                <>
                  {!multi && <BreakdownRow label="Avg call length" value={`${results.avgCallLengthSecs} sec`} />}
                  <BreakdownRow label="Total minutes" value={results.totalMinutes.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow label="AI Agent Units required" value={unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && solution.isNew && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'roundedMinute' && (
                <>
                  {!multi && <BreakdownRow label="Minutes per call (rounded up)" value={results.minutesPerCall.toLocaleString()} />}
                  <BreakdownRow label="Total minutes" value={results.totalMinutes.toLocaleString()} />
                  <BreakdownRow
                    label="Price per minute"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'concurrent' && (
                <>
                  <BreakdownRow label="Peak concurrent sessions" value={results.peakConcurrent.toLocaleString()} />
                  <BreakdownRow
                    label="Price per concurrent session"
                    value={`$${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
              {billingMode === 'second' && (
                <>
                  {!multi && <BreakdownRow label="Avg call length" value={`${results.avgCallLengthSecs} sec`} />}
                  <BreakdownRow label="Total seconds" value={results.totalSeconds.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
                  <BreakdownRow
                    label="Price per second"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'token' && (
                <>
                  {!multi && <BreakdownRow label="Sessions per call" value={results.sessionsPerCall.toLocaleString()} />}
                  <BreakdownRow label="Total sessions" value={results.totalSessions.toLocaleString()} />
                  <BreakdownRow label="Tokens required" value={results.tokensRequired.toLocaleString()} />
                  <BreakdownRow label={`Free tokens (${results.licenseType})`} value={results.freeTokens.toLocaleString()} />
                  <BreakdownRow label="Billable tokens" value={results.billableTokens.toLocaleString()} />
                  <BreakdownRow
                    label="Price per token"
                    value={`$${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
              {billingMode === 'digital' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Avg messages/session" value={results.avgMsgs} />
                  <BreakdownRow label="Normalized sessions" value={Math.ceil(results.normalizedSessions).toLocaleString()} />
                  <BreakdownRow label="AI Agent Units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'engagement' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow
                    label="Price per engagement"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'messageCredits' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Avg messages/interaction" value={results.avgMsgs} />
                  <BreakdownRow label="Total messages (credits)" value={Math.ceil(results.totalMessages).toLocaleString()} />
                  <BreakdownRow label="AI Agent Units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'perMessage' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Avg messages/interaction" value={results.avgMsgs} />
                  <BreakdownRow label="Total messages" value={Math.ceil(results.totalMessages).toLocaleString()} />
                  <BreakdownRow
                    label="Price per message"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'ciscoSplit' && (
                <>
                  <BreakdownRow label="Total seconds" value={results.splitTotalSeconds.toLocaleString()} />
                  <BreakdownRow label="Total minutes" value={results.splitTotalMinutes.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow label="Split" value={`${results.splitScriptedPct}% Scripted / ${100 - results.splitScriptedPct}% Autonomous`} />
                  <BreakdownRow label="Scripted minutes" value={results.scriptedMinutes.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
                  <BreakdownRow label="Scripted units" value={results.scriptedUnits.toLocaleString()} />
                  <BreakdownRow label="Autonomous minutes" value={results.autonomousMinutes.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
                  <BreakdownRow label="Autonomous units" value={results.autonomousUnits.toLocaleString()} />
                  <BreakdownRow label="Total units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'lex' && (
                <>
                  {!multi && results.numSpeechRequests !== null && (
                    <BreakdownRow label="Speech requests per call" value={results.numSpeechRequests.toLocaleString()} />
                  )}
                  <BreakdownRow label="Total speech requests" value={results.lexSpeechCost > 0 ? (results.lexSpeechCost / results.effectiveSpeechPrice).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0'} />
                  <BreakdownRow
                    label="Lex per speech request"
                    value={`$${results.effectiveSpeechPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`}
                  />
                  <BreakdownRow
                    label="Lex speech cost"
                    value={`$${results.lexSpeechCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                  <BreakdownRow
                    label="Connect rate (per min)"
                    value={`$${results.effectiveConnectPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`}
                  />
                  <BreakdownRow
                    label="Connect total"
                    value={`$${results.connectTotalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
            </div>

            {billingMode === 'lex' ? (
              <div className="lex-cost-blocks">
                <div className="cost-block">
                  <div className="cost-label">Lex Only (without Connect)</div>
                  <div className="cost-value">
                    ${results.totalCostWithoutConnect.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">
                    ${(results.totalCostWithoutConnect / results.numCalls).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per call
                  </div>
                </div>
                <div className="cost-block cost-block-connect">
                  <div className="cost-label">Lex + Connect Charges</div>
                  <div className="cost-value">
                    ${results.totalCostWithConnect.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">
                    ${(results.totalCostWithConnect / results.numCalls).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per call
                  </div>
                </div>
              </div>
            ) : billingMode === 'ciscoSplit' ? (
              <div className="lex-cost-blocks">
                <div className="cost-block">
                  <div className="cost-label">Scripted Tier</div>
                  <div className="cost-value">
                    ${results.scriptedCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">{results.scriptedUnits.toLocaleString()} units</div>
                </div>
                <div className="cost-block">
                  <div className="cost-label">Autonomous Tier</div>
                  <div className="cost-value">
                    ${results.autonomousCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">{results.autonomousUnits.toLocaleString()} units</div>
                </div>
                <div className="cost-block cost-block-connect">
                  <div className="cost-label">Blended Total</div>
                  <div className="cost-value">
                    ${results.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">{results.unitsRequired.toLocaleString()} units</div>
                </div>
              </div>
            ) : (
              <div className="cost-block">
                <div className="cost-label">Estimated List Price</div>
                <div className="cost-value">
                  ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                {['digital', 'engagement', 'messageCredits', 'perMessage'].includes(billingMode) && results.numInteractions > 0 && (
                  <div className="cost-per-call">
                    ${(totalCost / results.numInteractions).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per interaction
                  </div>
                )}
                {!['concurrent', 'digital', 'engagement', 'messageCredits', 'perMessage'].includes(billingMode) && results.numCalls > 0 && (
                  <div className="cost-per-call">
                    ${(totalCost / results.numCalls).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per call
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="placeholder-msg">
            {solution.billingMode === 'concurrent'
              ? 'Enter peak concurrent calls to see results'
              : solution.billingMode === 'lex'
              ? 'Enter call volume and speech requests to see results'
              : ['digital', 'engagement', 'messageCredits', 'perMessage'].includes(solution.billingMode)
              ? 'Enter digital interaction volume to see results'
              : solution.billingMode === 'ciscoSplit'
              ? 'Enter total seconds and scripted split % to see results'
              : 'Enter call volume details to see results'}
          </div>
        )}
      </div>
    </div>
  )
}

function BillingNote({ comparisonIds }) {
  const solutions = comparisonIds.map((id) => SOLUTIONS[id])
  const hasSession = solutions.some((s) => s.billingMode === 'session')
  const hasMinute = solutions.some((s) => s.billingMode === 'minute')
  const hasConcurrent = solutions.some((s) => s.billingMode === 'concurrent')
  const hasSecond = solutions.some((s) => s.billingMode === 'second')
  const hasLex = solutions.some((s) => s.billingMode === 'lex')
  const hasToken = solutions.some((s) => s.billingMode === 'token')
  const hasDigital = solutions.some((s) => s.billingMode === 'digital')
  const hasRoundedMinute = solutions.some((s) => s.billingMode === 'roundedMinute')
  const engagementSolutions = solutions.filter((s) => s.billingMode === 'engagement')
  const hasMessageCredits = solutions.some((s) => s.billingMode === 'messageCredits')
  const hasPerMessage = solutions.some((s) => s.billingMode === 'perMessage')
  const hasCiscoSplit = solutions.some((s) => s.billingMode === 'ciscoSplit')

  if (!hasSession && !hasMinute && !hasConcurrent && !hasSecond && !hasLex && !hasToken && !hasDigital && !hasRoundedMinute && engagementSolutions.length === 0 && !hasMessageCredits && !hasPerMessage && !hasCiscoSplit) return null

  return (
    <div className="billing-notes">
      {hasSession && (
        <div className="billing-note session">
          <strong>Old model:</strong> voice sessions billed in {SESSION_BLOCK_SECONDS}-second ({SESSION_BLOCK_SECONDS / 60}-min) blocks, rounded up.
          A 150-sec call = 2 sessions.
        </div>
      )}
      {hasMinute && (
        <div className="billing-note minute">
          <strong>New model:</strong> voice usage billed continuously, metered to the second.
          A 150-sec call = 2.5 minutes consumed.
        </div>
      )}
      {hasConcurrent && (
        <div className="billing-note concurrent">
          <strong>Five9:</strong> priced per concurrent IVA session.
          Cost = peak concurrent calls × ${FIVE9_PRICE_PER_SESSION}/session.
        </div>
      )}
      {hasSecond && (
        <div className="billing-note second">
          <strong>Google:</strong> priced per second of voice usage.
          Cost = total calls × avg call length (sec) × price/sec.
        </div>
      )}
      {hasLex && (
        <div className="billing-note lex">
          <strong>Amazon Connect + Lex:</strong> Lex billed per speech request (${AMAZON_LEX_SPEECH_PRICE}).
          Connect charges shown separately — Basic: ${AMAZON_CONNECT_BASIC_PRICE_PER_MIN}/min · AI Bundled: ${AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN}/min (no extra AI charges).
        </div>
      )}
      {hasToken && (
        <div className="billing-note token">
          <strong>Genesys:</strong> billed per token · {GENESYS_SESSIONS_PER_TOKEN} sessions/token · 2-min session blocks.
          Named: {GENESYS_FREE_TOKENS_NAMED} free tokens · Concurrent: {GENESYS_FREE_TOKENS_CONCURRENT} free tokens per period.
        </div>
      )}
      {hasDigital && (
        <div className="billing-note minute">
          <strong>Cisco Digital:</strong> billed per AI Agent Unit based on {DIGITAL_MESSAGES_PER_STANDARD_SESSION}-message session standard.
          Interactions with more or fewer messages are normalized proportionally.
          Scripted: {CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit · Autonomous: {CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit.
        </div>
      )}
      {hasRoundedMinute && (
        <div className="billing-note minute">
          <strong>Zoom Voice:</strong> priced per minute, rounded up to the nearest minute per call.
          Cost = total calls × ⌈avg call length ÷ 60⌉ × ${ZOOM_VOICE_PRICE_PER_MIN}/min.
        </div>
      )}
      {engagementSolutions.map((s) => (
        <div className="billing-note digital" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> flat rate per engagement/session, no message-based proration.
          Cost = digital interactions × ${s.unitPrice}/engagement.
        </div>
      ))}
      {hasMessageCredits && (
        <div className="billing-note digital">
          <strong>Five9 Digital:</strong> billed in credit-pack units, no message normalization.
          Cost = ⌈(digital interactions × avg messages) ÷ {FIVE9_DIGITAL_CREDITS_PER_UNIT.toLocaleString()}⌉ × ${FIVE9_DIGITAL_LIST_PRICE}/unit — 1 credit per message sent or received.
        </div>
      )}
      {hasPerMessage && (
        <div className="billing-note digital">
          <strong>Amazon Connect Digital:</strong> flat rate per message, no bundling.
          Cost = digital interactions × avg messages × ${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE}/message.
        </div>
      )}
      {hasCiscoSplit && (
        <div className="billing-note minute">
          <strong>Cisco Split Agent:</strong> total minutes split by Scripted/Autonomous %, each tier packed into its own unit size and summed.
          Scripted: {NEW_SCRIPTED_MINUTES_PER_UNIT.toLocaleString()} min/unit · Autonomous: {NEW_AUTONOMOUS_MINUTES_PER_UNIT.toLocaleString()} min/unit.
        </div>
      )}
    </div>
  )
}

function ComparisonTable({ selectedIds, results, solutionIsReady }) {
  const readyIds = selectedIds.filter((id) => solutionIsReady(id))
  if (readyIds.length === 0) return null

  const fmt2 = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const fmt4 = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })

  const BILLING_LABELS = {
    session: 'Per Session (2-min blocks)',
    minute: 'Per Minute (to the second)',
    roundedMinute: 'Per Minute (rounded up)',
    concurrent: 'Per Concurrent Session',
    second: 'Per Second',
    lex: 'Lex + Connect',
    token: 'Per Token (w/ free tier)',
    digital: 'Per 10-Message Session',
    engagement: 'Per Engagement',
    messageCredits: 'Per Credit (Message)',
    perMessage: 'Per Message (no bundling)',
    ciscoSplit: 'Blended Scripted/Autonomous',
  }

  function getPrimaryTotal(id) {
    const r = results[id]
    if (!r) return null
    return r.billingMode === 'lex' ? r.totalCostWithoutConnect : r.totalCost
  }

  function getSecondaryTotal(id) {
    const r = results[id]
    if (!r || r.billingMode !== 'lex') return null
    return r.totalCostWithConnect
  }

  function getCostPerCall(id) {
    const r = results[id]
    if (!r) return null
    if (r.billingMode === 'concurrent' || r.billingMode === 'ciscoSplit') return null
    if (r.billingMode === 'lex') return r.totalCostWithoutConnect / r.numCalls
    if (['digital', 'engagement', 'messageCredits', 'perMessage'].includes(r.billingMode)) return r.totalCost / r.numInteractions
    return r.totalCost / r.numCalls
  }

  return (
    <div className="comparison-table-wrap">
      <table className="comparison-table">
        <thead>
          <tr>
            <th className="table-label-col"></th>
            {readyIds.map((id) => {
              const s = SOLUTIONS[id]
              return (
                <th key={id} style={{ borderTopColor: s.accentColor }}>
                  <div className="table-head-name">{s.name}</div>
                  <div className="table-head-subtitle" style={{ color: s.accentColor }}>{s.subtitle}</div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="table-row-label">Billing Mode</td>
            {readyIds.map((id) => (
              <td key={id}>{BILLING_LABELS[SOLUTIONS[id].billingMode] ?? SOLUTIONS[id].billingMode}</td>
            ))}
          </tr>
          <tr className="table-highlight-row">
            <td className="table-row-label">Total Cost</td>
            {readyIds.map((id) => {
              const primary = getPrimaryTotal(id)
              const secondary = getSecondaryTotal(id)
              return (
                <td key={id}>
                  <div className="table-cost-cell">
                    <span className="table-primary-cost">${fmt2(primary)}</span>
                    {secondary !== null && (
                      <span className="table-secondary-cost">${fmt2(secondary)} w/ Connect</span>
                    )}
                  </div>
                </td>
              )
            })}
          </tr>
          <tr>
            <td className="table-row-label">Cost per Call</td>
            {readyIds.map((id) => {
              const cpc = getCostPerCall(id)
              return <td key={id}>{cpc !== null ? `$${fmt4(cpc)}` : '—'}</td>
            })}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function ScenarioBreakdownTable({ selectedIds, scenarios, results, solutionIsReady }) {
  const readyIds = selectedIds.filter((id) => solutionIsReady(id))
  const readyScenarios = scenarios.filter(s => Number(s.numCalls) > 0 && Number(s.avgCallLength) > 0)

  if (readyIds.length === 0 || readyScenarios.length < 2) return null

  const fmt2 = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  function getScenarioCost(solutionId, scenarioId) {
    const r = results[solutionId]
    if (!r || !r.scenarioResults) return null
    const sr = r.scenarioResults.find(x => x.scenarioId === scenarioId)
    if (!sr) return null
    return sr.billingMode === 'lex' ? sr.totalCostWithoutConnect : sr.totalCost
  }

  function getPrimaryTotal(solutionId) {
    const r = results[solutionId]
    if (!r) return null
    return r.billingMode === 'lex' ? r.totalCostWithoutConnect : r.totalCost
  }

  return (
    <div className="scenario-breakdown-wrap">
      <h3 className="scenario-breakdown-title">Scenario Breakdown</h3>
      <div className="comparison-table-wrap">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="table-label-col"></th>
              {readyIds.map((id) => {
                const s = SOLUTIONS[id]
                return (
                  <th key={id} style={{ borderTopColor: s.accentColor }}>
                    <div className="table-head-name">{s.name}</div>
                    <div className="table-head-subtitle" style={{ color: s.accentColor }}>{s.subtitle}</div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {readyScenarios.map((s, i) => (
              <tr key={s.id}>
                <td className="table-row-label">
                  <div>Scenario {i + 1}</div>
                  <div className="scenario-cell-note">{Number(s.numCalls).toLocaleString()} calls · {Number(s.avgCallLength)}s avg</div>
                </td>
                {readyIds.map((id) => {
                  const isConcurrent = SOLUTIONS[id].billingMode === 'concurrent'
                  const cost = isConcurrent ? null : getScenarioCost(id, s.id)
                  return <td key={id}>{cost !== null ? `$${fmt2(cost)}` : '—'}</td>
                })}
              </tr>
            ))}
            <tr className="table-highlight-row">
              <td className="table-row-label">Total</td>
              {readyIds.map((id) => {
                const total = getPrimaryTotal(id)
                return (
                  <td key={id}>
                    <div className="table-cost-cell">
                      <span className="table-primary-cost">{total !== null ? `$${fmt2(total)}` : '—'}</span>
                    </div>
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── ROI Line Chart ───────────────────────────────────────────────────

function RoiLineChart({ rows }) {
  if (!rows || rows.length < 2) return null
  const hasConservative = rows.some(r => r.conservative !== null)
  const hasAggressive = rows.some(r => r.aggressive !== null)
  const W = 800, H = 280
  const pL = 75, pR = 24, pT = 30, pB = 36
  const iW = W - pL - pR, iH = H - pT - pB
  const n = rows.length
  const allVals = rows.flatMap(r => {
    const v = [r.cumulativeCurrent, r.expected.cumulativeAI]
    if (r.conservative) v.push(r.conservative.cumulativeAI)
    if (r.aggressive) v.push(r.aggressive.cumulativeAI)
    return v
  })
  const yMax = Math.max(...allVals) * 1.05
  const xOf = (m) => pL + ((m - 1) / (n - 1)) * iW
  const yOf = (v) => pT + iH - (v / yMax) * iH
  const makePath = (vals) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i + 1).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ')
  const fmt = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => yMax * f)
  const step = Math.ceil(n / 6)
  const xTickMonths = rows.filter((r, i) => i === 0 || (i + 1) % step === 0 || i === n - 1).map(r => r.month)
  const COLORS = { current: '#ff6b6b', conservative: '#74c0fc', expected: '#69db7c', aggressive: '#ffa94d' }
  const legendItems = [
    { label: 'Current Cost', color: COLORS.current, dash: false },
    hasConservative && { label: 'Conservative', color: COLORS.conservative, dash: true },
    { label: 'Expected', color: COLORS.expected, dash: false },
    hasAggressive && { label: 'Aggressive', color: COLORS.aggressive, dash: true },
  ].filter(Boolean)
  return (
    <div className="roi-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={pL} y1={yOf(v)} x2={W - pR} y2={yOf(v)} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <text x={pL - 6} y={yOf(v) + 4} textAnchor="end" fill="rgba(255,255,255,0.45)" fontSize="11">{fmt(v)}</text>
          </g>
        ))}
        {xTickMonths.map(m => (
          <text key={m} x={xOf(m)} y={H - pB + 14} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="11">Mo {m}</text>
        ))}
        <path d={makePath(rows.map(r => r.cumulativeCurrent))} fill="none" stroke={COLORS.current} strokeWidth="2.5" />
        {hasConservative && <path d={makePath(rows.map(r => r.conservative.cumulativeAI))} fill="none" stroke={COLORS.conservative} strokeWidth="2" strokeDasharray="5,3" />}
        <path d={makePath(rows.map(r => r.expected.cumulativeAI))} fill="none" stroke={COLORS.expected} strokeWidth="2.5" />
        {hasAggressive && <path d={makePath(rows.map(r => r.aggressive.cumulativeAI))} fill="none" stroke={COLORS.aggressive} strokeWidth="2" strokeDasharray="5,3" />}
        {legendItems.map((item, i) => (
          <g key={i} transform={`translate(${pL + i * 132}, ${pT - 16})`}>
            <line x1="0" y1="0" x2="22" y2="0" stroke={item.color} strokeWidth="2.5" strokeDasharray={item.dash ? '5,3' : undefined} />
            <text x="26" y="4" fill="rgba(255,255,255,0.75)" fontSize="11">{item.label}</text>
          </g>
        ))}
      </svg>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────

function AiCalculatorApp({ onBackToPlatformSelector, onBackToHome }) {
  const [scenarios, setScenarios] = useState([
    { id: 1, numCalls: '', avgCallLength: '' }
  ])
  const [nextScenarioId, setNextScenarioId] = useState(2)
  const [peakConcurrent, setPeakConcurrent] = useState('')
  const [customerUnitPrice, setCustomerUnitPrice] = useState('')
  const [five9CustomerPrice, setFive9CustomerPrice] = useState('')
  const [googleFlowsCustomerPrice, setGoogleFlowsCustomerPrice] = useState('')
  const [googlePlaybooksCustomerPrice, setGooglePlaybooksCustomerPrice] = useState('')
  const [amazonSpeechReqsPerCall, setAmazonSpeechReqsPerCall] = useState('')
  const [amazonLexCustomerPrice, setAmazonLexCustomerPrice] = useState('')
  const [amazonConnectCustomerPrice, setAmazonConnectCustomerPrice] = useState('')
  const [genesysCustomerPrice, setGenesysCustomerPrice] = useState('')
  const [genesysDigitalCustomerPrice, setGenesysDigitalCustomerPrice] = useState('')
  const [zoomVoiceCustomerPrice, setZoomVoiceCustomerPrice] = useState('')
  const [zoomDigitalCustomerPrice, setZoomDigitalCustomerPrice] = useState('')
  const [five9DigitalCustomerPrice, setFive9DigitalCustomerPrice] = useState('')
  const [amazonConnectDigitalCustomerPrice, setAmazonConnectDigitalCustomerPrice] = useState('')
  const [googleDigitalCustomerPrice, setGoogleDigitalCustomerPrice] = useState('')
  const [splitCustomerPrice, setSplitCustomerPrice] = useState('')
  const [splitTotalSeconds, setSplitTotalSeconds] = useState('')
  const [splitScriptedPct, setSplitScriptedPct] = useState('')
  const [numDigitalInteractions, setNumDigitalInteractions] = useState('')
  const [avgMessagesPerSession, setAvgMessagesPerSession] = useState('10')
  // digitalCustomerPrice removed — digital uses same customerUnitPrice as voice
  const [channelType, setChannelType] = useState('voice')
  const [licenseType, setLicenseType] = useState('named')
  const [selectedIds, setSelectedIds] = useState(['new_scripted', 'new_autonomous'])
  const [callVolumeOpen, setCallVolumeOpen] = useState(true)
  const [pricingOpen, setPricingOpen] = useState(true)
  const [calcTab, setCalcTab] = useState('calculator')
  const [roiCurrentCost, setRoiCurrentCost] = useState('')
  const [roiPS, setRoiPS] = useState('')
  const [roiMonthlyUsage, setRoiMonthlyUsage] = useState('')
  const [roiMonthlyConservative, setRoiMonthlyConservative] = useState('')
  const [roiMonthlyAggressive, setRoiMonthlyAggressive] = useState('')
  const [roiTimeline, setRoiTimeline] = useState('36')

  function scenarioIsVolumeReady(s) {
    return Number(s.numCalls) > 0 && Number(s.avgCallLength) > 0
  }

  function effectiveSpeechReqsForScenario(s) {
    return amazonSpeechReqsPerCall !== ''
      ? Number(amazonSpeechReqsPerCall)
      : (Number(s.avgCallLength) / 60) * 4
  }

  const isVolumeReady = scenarios.some(scenarioIsVolumeReady)
  const isPeakReady = Number(peakConcurrent) > 0
  const isDigitalReady = Number(numDigitalInteractions) > 0
  const isSplitReady = Number(splitTotalSeconds) > 0 && splitScriptedPct !== '' && Number(splitScriptedPct) >= 0 && Number(splitScriptedPct) <= 100

  function addScenario() {
    setScenarios(prev => [...prev, { id: nextScenarioId, numCalls: '', avgCallLength: '' }])
    setNextScenarioId(n => n + 1)
  }

  function removeScenario(id) {
    setScenarios(prev => prev.filter(s => s.id !== id))
  }

  function updateScenario(id, field, value) {
    setScenarios(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s))
  }

  function solutionIsReady(id) {
    const mode = SOLUTIONS[id].billingMode
    if (mode === 'concurrent') return isPeakReady
    if (mode === 'digital' || mode === 'engagement' || mode === 'messageCredits' || mode === 'perMessage') return isDigitalReady
    if (mode === 'ciscoSplit') return isSplitReady
    return isVolumeReady
  }

  function toggleSolution(id) {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.length > 1 ? prev.filter((s) => s !== id) : prev
        : [...prev, id].sort((a, b) => SOLUTION_ORDER.indexOf(a) - SOLUTION_ORDER.indexOf(b))
    )
  }

  function getOverridePrice(id) {
    if (id === 'amazon_connect_basic' || id === 'amazon_connect_ai_bundled') return Number(amazonLexCustomerPrice) || 0
    if (id === 'amazon_connect_basic_digital' || id === 'amazon_connect_ai_bundled_digital') return Number(amazonConnectDigitalCustomerPrice) || 0
    if (id === 'five9_iva') return Number(five9CustomerPrice) || 0
    if (id === 'five9_digital') return Number(five9DigitalCustomerPrice) || 0
    if (id === 'google_flows') return Number(googleFlowsCustomerPrice) || 0
    if (id === 'google_playbooks') return Number(googlePlaybooksCustomerPrice) || 0
    if (id === 'google_digital') return Number(googleDigitalCustomerPrice) || 0
    if (id === 'genesys') return Number(genesysCustomerPrice) || 0
    if (id === 'genesys_digital') return Number(genesysDigitalCustomerPrice) || 0
    if (id === 'zoom_voice') return Number(zoomVoiceCustomerPrice) || 0
    if (id === 'zoom_digital') return Number(zoomDigitalCustomerPrice) || 0
    if (id === 'new_split') return Number(splitCustomerPrice) || 0
    if (id === 'cisco_digital_scripted' || id === 'cisco_digital_autonomous') return Number(customerUnitPrice) || 0
    if (SOLUTIONS[id].isNew) return Number(customerUnitPrice) || 0
    return 0
  }

  function getOverrideConnectPrice(id) {
    if (id === 'amazon_connect_basic' || id === 'amazon_connect_ai_bundled') return Number(amazonConnectCustomerPrice) || 0
    return 0
  }

  const results = useMemo(() => {
    return selectedIds.reduce((acc, id) => {
      const solution = SOLUTIONS[id]
      if (solution.billingMode === 'concurrent') {
        if (!isPeakReady) return acc
        const r = calculateResults({
          numCalls: 0, avgCallLength: 0, peakConcurrent: Number(peakConcurrent),
          numSpeechRequests: 0, licenseType, solution,
          overridePrice: getOverridePrice(id), overrideConnectPrice: getOverrideConnectPrice(id),
        })
        acc[id] = { ...r, scenarioResults: [] }
        return acc
      }
      if (['digital', 'engagement', 'messageCredits', 'perMessage'].includes(solution.billingMode)) {
        if (!isDigitalReady) return acc
        const r = calculateResults({
          solution,
          numDigitalInteractions: Number(numDigitalInteractions),
          avgMessagesPerSession: Number(avgMessagesPerSession),
          overridePrice: getOverridePrice(id),
        })
        acc[id] = { ...r, scenarioResults: [] }
        return acc
      }
      if (solution.billingMode === 'ciscoSplit') {
        if (!isSplitReady) return acc
        const r = calculateResults({
          solution,
          splitTotalSeconds: Number(splitTotalSeconds),
          splitScriptedPct: Number(splitScriptedPct),
          overridePrice: getOverridePrice(id),
        })
        acc[id] = { ...r, scenarioResults: [] }
        return acc
      }
      const readyScenarios = scenarios.filter(scenarioIsVolumeReady)
      if (readyScenarios.length === 0) return acc
      const scenarioResults = readyScenarios.map(s => ({
        scenarioId: s.id,
        numCalls: Number(s.numCalls),
        avgCallLength: Number(s.avgCallLength),
        ...calculateResults({
          numCalls: Number(s.numCalls),
          avgCallLength: Number(s.avgCallLength),
          peakConcurrent: 0,
          numSpeechRequests: effectiveSpeechReqsForScenario(s),
          licenseType,
          solution,
          overridePrice: getOverridePrice(id),
          overrideConnectPrice: getOverrideConnectPrice(id),
        }),
      }))
      acc[id] = { ...aggregateScenarioResults(scenarioResults, solution.billingMode), scenarioResults }
      return acc
    }, {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, peakConcurrent, licenseType, customerUnitPrice, five9CustomerPrice, five9DigitalCustomerPrice, googleFlowsCustomerPrice, googlePlaybooksCustomerPrice, googleDigitalCustomerPrice, amazonSpeechReqsPerCall, amazonLexCustomerPrice, amazonConnectCustomerPrice, amazonConnectDigitalCustomerPrice, genesysCustomerPrice, genesysDigitalCustomerPrice, zoomVoiceCustomerPrice, zoomDigitalCustomerPrice, splitCustomerPrice, splitTotalSeconds, splitScriptedPct, numDigitalInteractions, avgMessagesPerSession, selectedIds, isVolumeReady, isPeakReady, isDigitalReady, isSplitReady])

  const roiRows = useMemo(() => {
    const currentCost = Number(roiCurrentCost)
    const ps = Number(roiPS)
    const expectedMonthly = Number(roiMonthlyUsage)
    const conservativeMonthly = roiMonthlyConservative !== '' ? Number(roiMonthlyConservative) : null
    const aggressiveMonthly = roiMonthlyAggressive !== '' ? Number(roiMonthlyAggressive) : null
    const months = roiTimeline === '' || Number(roiTimeline) === 0 ? 36 : Math.min(Math.max(Math.floor(Number(roiTimeline)), 1), 120)
    if (currentCost <= 0) return []
    let bkExpected = false, bkConservative = false, bkAggressive = false
    return Array.from({ length: months }, (_, i) => {
      const month = i + 1
      const cumulativeCurrent = currentCost * month
      const cumExpected = ps + expectedMonthly * month
      const netExpected = cumulativeCurrent - cumExpected
      const isBkExpected = !bkExpected && netExpected >= 0
      if (isBkExpected) bkExpected = true
      let conservative = null
      if (conservativeMonthly !== null) {
        const cumAI = ps + conservativeMonthly * month
        const net = cumulativeCurrent - cumAI
        const isBK = !bkConservative && net >= 0
        if (isBK) bkConservative = true
        conservative = { cumulativeAI: cumAI, netSavings: net, isBreakEven: isBK }
      }
      let aggressive = null
      if (aggressiveMonthly !== null) {
        const cumAI = ps + aggressiveMonthly * month
        const net = cumulativeCurrent - cumAI
        const isBK = !bkAggressive && net >= 0
        if (isBK) bkAggressive = true
        aggressive = { cumulativeAI: cumAI, netSavings: net, isBreakEven: isBK }
      }
      return { month, cumulativeCurrent, expected: { cumulativeAI: cumExpected, netSavings: netExpected, isBreakEven: isBkExpected }, conservative, aggressive }
    })
  }, [roiCurrentCost, roiPS, roiMonthlyUsage, roiMonthlyConservative, roiMonthlyAggressive, roiTimeline])

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="ai-calculator-root">
        <nav className="calc-back-nav">
          {/* Top-left navigation: exit to platform selector + return to the SA Tools hub */}
          <PlatformNavBar
            onBackToPlatformSelector={onBackToPlatformSelector}
            onBackToHome={onBackToHome}
            showHome={!!onBackToHome}
            homeLabel="SA Tools"
            color="purple"
            mb={0}
          />
        </nav>

        <header className="app-header">
          <div className="header-inner">
            <div className="header-title">
              <div className="brand-name">Presidio</div>
              <h1>AI Agent Calculator</h1>
              <p>Estimate licensing costs across AI Agent solutions</p>
            </div>
          </div>
        </header>

        <div className="calc-tab-bar">
          <button className={`calc-tab${calcTab === 'calculator' ? ' active' : ''}`} onClick={() => setCalcTab('calculator')}>
            Calculator
          </button>
          <button className={`calc-tab${calcTab === 'roi' ? ' active' : ''}`} onClick={() => setCalcTab('roi')}>
            ROI Break-Even
          </button>
        </div>

        {calcTab === 'calculator' && <main className="app-main">
          <section className="inputs-section">
            <button className="section-toggle" onClick={() => setCallVolumeOpen((o) => !o)} aria-expanded={callVolumeOpen}>
              <h2>Call Volume Inputs</h2>
              <span className={`section-chevron${callVolumeOpen ? ' open' : ''}`}>&#8964;</span>
            </button>
            {callVolumeOpen && (
              <div className="call-volume-content">
                <div className="inputs-grid call-volume-top">
                  <div className="input-field">
                    <label>Agent License Type</label>
                    <span className="input-hint">Licensing model for this deployment</span>
                    <div className="radio-toggle">
                      {[{ value: 'named', label: 'Named' }, { value: 'concurrent', label: 'Concurrent' }].map(({ value, label }) => (
                        <label key={value} className={`radio-toggle-option${licenseType === value ? ' selected' : ''}`}>
                          <input
                            type="radio"
                            name="licenseType"
                            value={value}
                            checked={licenseType === value}
                            onChange={() => setLicenseType(value)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <InputField
                    label="Peak Concurrent Calls"
                    hint="Maximum simultaneous calls at peak (Five9 only)"
                    value={peakConcurrent}
                    onChange={setPeakConcurrent}
                    min={1}
                  />
                </div>

                <div className="inputs-grid call-volume-top">
                  <InputField
                    label="Cisco Split — Total Seconds"
                    hint="Total blended usage in seconds for the Cisco AI Agent Split card"
                    value={splitTotalSeconds}
                    onChange={setSplitTotalSeconds}
                    min={1}
                    suffix="sec"
                  />
                  <InputField
                    label="Cisco Split — Scripted %"
                    hint="Percent Scripted; remainder is billed Autonomous"
                    value={splitScriptedPct}
                    onChange={setSplitScriptedPct}
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </div>
                {(splitScriptedPct !== '' || splitTotalSeconds !== '') && (
                  <div className="input-hint" style={{ marginBottom: '12px' }}>
                    {splitTotalSeconds !== '' && <>{(Number(splitTotalSeconds) / 60).toLocaleString('en-US', { maximumFractionDigits: 2 })} min total · </>}
                    {splitScriptedPct !== '' && <>{Number(splitScriptedPct)}% Scripted → {100 - Number(splitScriptedPct)}% Autonomous</>}
                  </div>
                )}

                <div className="input-field" style={{ marginBottom: '12px' }}>
                  <label>Channel Type</label>
                  <span className="input-hint">Select the interaction channel to configure volume inputs</span>
                  <div className="radio-toggle">
                    {[{ value: 'voice', label: 'Voice' }, { value: 'digital', label: 'Digital' }].map(({ value, label }) => (
                      <label key={value} className={`radio-toggle-option${channelType === value ? ' selected' : ''}`}>
                        <input
                          type="radio"
                          name="channelType"
                          value={value}
                          checked={channelType === value}
                          onChange={() => setChannelType(value)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {channelType === 'voice' && (
                  <>
                    <div className="scenarios-list">
                      {scenarios.map((s, i) => (
                        <div key={s.id} className="scenario-row">
                          <div className="scenario-row-header">
                            <span className="scenario-label">Scenario {i + 1}</span>
                            {scenarios.length > 1 && (
                              <button className="scenario-remove-btn" onClick={() => removeScenario(s.id)} aria-label="Remove scenario">✕</button>
                            )}
                          </div>
                          <div className="scenario-row-inputs">
                            <InputField
                              label="Number of Calls"
                              hint="Total calls in billing period"
                              value={s.numCalls}
                              onChange={v => updateScenario(s.id, 'numCalls', v)}
                              min={1}
                            />
                            <InputField
                              label="Avg Call Length"
                              hint="Mean duration per call in seconds"
                              value={s.avgCallLength}
                              onChange={v => updateScenario(s.id, 'avgCallLength', v)}
                              min={1}
                              step={1}
                              suffix="sec"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="scenario-add-btn" onClick={addScenario}>+ Add Scenario</button>
                  </>
                )}

                {channelType === 'digital' && (
                  <div className="inputs-grid">
                    <InputField
                      label="Number of Digital Interactions"
                      hint="Total digital (chat/messaging) interactions in billing period"
                      value={numDigitalInteractions}
                      onChange={setNumDigitalInteractions}
                      min={1}
                    />
                    <InputField
                      label="Avg Messages per Session"
                      hint="Average messages per digital interaction — used by message-based billing models below"
                      value={avgMessagesPerSession}
                      onChange={setAvgMessagesPerSession}
                      min={1}
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="inputs-section">
            <button className="section-toggle" onClick={() => setPricingOpen((o) => !o)} aria-expanded={pricingOpen}>
              <h2>Pricing Considerations</h2>
              <span className={`section-chevron${pricingOpen ? ' open' : ''}`}>&#8964;</span>
            </button>
            {pricingOpen && <div className="inputs-grid">
              <div className="input-field customer-price-field">
                <label>Cisco AI Agent per Unit</label>
                <span className="input-hint">Overrides list price for new Cisco AI Agent (voice &amp; digital)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={customerUnitPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${NEW_UNIT_LIST_PRICE} (list)`}
                    onChange={(e) => setCustomerUnitPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field customer-price-field">
                <label>Cisco AI Agent Split per Unit</label>
                <span className="input-hint">Overrides list price for the Cisco AI Agent Split (Scripted + Autonomous blend)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={splitCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${NEW_UNIT_LIST_PRICE} (list)`}
                    onChange={(e) => setSplitCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field five9-price-field">
                <label>Five9 Voice IVA per Session</label>
                <span className="input-hint">Overrides list price for Five9 Voice IVA</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={five9CustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${FIVE9_PRICE_PER_SESSION} (list)`}
                    onChange={(e) => setFive9CustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field five9-price-field">
                <label>Five9 Digital IVA per Unit</label>
                <span className="input-hint">Overrides list price for Five9 Digital IVA (credit-pack units)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={five9DigitalCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${FIVE9_DIGITAL_LIST_PRICE} (list)`}
                    onChange={(e) => setFive9DigitalCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field google-price-field">
                <label>Google Flows per Second</label>
                <span className="input-hint">Overrides list price for Google Conversational Agents (Flows)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={googleFlowsCustomerPrice}
                    min={0}
                    step={0.0001}
                    placeholder={`${GOOGLE_FLOWS_PRICE_PER_SECOND} (list)`}
                    onChange={(e) => setGoogleFlowsCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field google-price-field">
                <label>Google Playbooks per Second</label>
                <span className="input-hint">Overrides list price for Google Conversational Agents (Playbooks)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={googlePlaybooksCustomerPrice}
                    min={0}
                    step={0.0001}
                    placeholder={`${GOOGLE_PLAYBOOKS_PRICE_PER_SECOND} (list)`}
                    onChange={(e) => setGooglePlaybooksCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field google-price-field">
                <label>Google Digital per Session</label>
                <span className="input-hint">Overrides list price for Google Conversational Agents (Digital)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={googleDigitalCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${GOOGLE_DIGITAL_PRICE_PER_SESSION} (list)`}
                    onChange={(e) => setGoogleDigitalCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field amazon-speech-field">
                <label>Amazon Lex — Speech Requests per Call</label>
                <span className="input-hint">Default: 4 per minute of call length. Override sets a fixed total per call.</span>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={amazonSpeechReqsPerCall}
                    min={0}
                    step={1}
                    placeholder="4/min (default)"
                    onChange={(e) => setAmazonSpeechReqsPerCall(e.target.value)}
                  />
                  <span className="input-adornment suffix">req</span>
                </div>
              </div>
              <div className="input-field amazon-price-field">
                <label>Amazon Lex per Speech Request</label>
                <span className="input-hint">Overrides list price for Lex speech requests</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={amazonLexCustomerPrice}
                    min={0}
                    step={0.0001}
                    placeholder={`${AMAZON_LEX_SPEECH_PRICE} (list)`}
                    onChange={(e) => setAmazonLexCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field amazon-price-field">
                <label>Amazon Connect per Minute</label>
                <span className="input-hint">Overrides Connect per-minute charge for whichever tier(s) are selected below</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={amazonConnectCustomerPrice}
                    min={0}
                    step={0.0001}
                    placeholder={`Basic ${AMAZON_CONNECT_BASIC_PRICE_PER_MIN} / Bundled ${AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN} (list)`}
                    onChange={(e) => setAmazonConnectCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field amazon-price-field">
                <label>Amazon Connect Digital per Message</label>
                <span className="input-hint">Overrides list price for Amazon Connect Digital (both tiers)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={amazonConnectDigitalCustomerPrice}
                    min={0}
                    step={0.0001}
                    placeholder={`${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE} (list)`}
                    onChange={(e) => setAmazonConnectDigitalCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field genesys-price-field">
                <label>Genesys per Token</label>
                <span className="input-hint">Overrides list price per token for Genesys AI Agent</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={genesysCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${GENESYS_PRICE_PER_TOKEN} (list)`}
                    onChange={(e) => setGenesysCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field genesys-price-field">
                <label>Genesys Digital per Session</label>
                <span className="input-hint">Overrides list price for Genesys Digital</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={genesysDigitalCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${GENESYS_DIGITAL_PRICE_PER_SESSION} (list)`}
                    onChange={(e) => setGenesysDigitalCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field zoom-price-field">
                <label>Zoom Voice per Minute</label>
                <span className="input-hint">Overrides list price for Zoom Contact Center Virtual Agent (Voice)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={zoomVoiceCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${ZOOM_VOICE_PRICE_PER_MIN} (list)`}
                    onChange={(e) => setZoomVoiceCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="input-field zoom-price-field">
                <label>Zoom Digital per Engagement</label>
                <span className="input-hint">Overrides list price for Zoom Contact Center Virtual Agent (Digital)</span>
                <div className="input-wrapper">
                  <span className="input-adornment prefix">$</span>
                  <input
                    type="number"
                    value={zoomDigitalCustomerPrice}
                    min={0}
                    step={0.01}
                    placeholder={`${ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT} (list)`}
                    onChange={(e) => setZoomDigitalCustomerPrice(e.target.value)}
                  />
                </div>
              </div>
            </div>}
          </section>

          <section className="results-section">
            <div className="results-header">
              <h2>Solution Comparison</h2>
            </div>

            <div className="solution-checkboxes">
              {SOLUTION_ORDER.map((id) => {
                const s = SOLUTIONS[id]
                const checked = selectedIds.includes(id)
                return (
                  <label
                    key={id}
                    className={`solution-checkbox-item${checked ? ' checked' : ''}`}
                    style={{ '--accent': s.accentColor }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSolution(id)}
                    />
                    <span className="checkbox-solution-name">{s.name}</span>
                    <span className="checkbox-solution-subtitle" style={{ color: checked ? s.accentColor : undefined }}>
                      {s.subtitle}
                    </span>
                  </label>
                )
              })}
            </div>

            <BillingNote comparisonIds={selectedIds} />

            <ComparisonTable
              selectedIds={selectedIds}
              results={results}
              solutionIsReady={solutionIsReady}
            />

            {(isVolumeReady || isPeakReady || isDigitalReady || isSplitReady) && (
              <div className="assumptions-bar">
                <span className="assumptions-label">Assumptions</span>
                {scenarios.filter(scenarioIsVolumeReady).map((s, i) => (
                  <span key={s.id} className="assumption-chip">
                    Scenario {i + 1}: {Number(s.numCalls).toLocaleString()} calls · {Number(s.avgCallLength)}s avg
                  </span>
                ))}
                {isPeakReady && (
                  <span className="assumption-chip">{Number(peakConcurrent).toLocaleString()} peak concurrent</span>
                )}
                {isDigitalReady && (
                  <span className="assumption-chip">{Number(numDigitalInteractions).toLocaleString()} digital interactions · {avgMessagesPerSession} msg/session avg</span>
                )}
                {isSplitReady && (
                  <span className="assumption-chip">{Number(splitTotalSeconds).toLocaleString()} sec split · {Number(splitScriptedPct)}% Scripted</span>
                )}
                <span className="assumption-chip">{licenseType === 'named' ? 'Named' : 'Concurrent'} license</span>
              </div>
            )}

            <div className="results-grid">
              {selectedIds.map((id) => (
                <ResultCard
                  key={id}
                  solution={SOLUTIONS[id]}
                  results={results[id] ?? {
                    billingMode: SOLUTIONS[id].billingMode,
                    sessionsPerCall: 0, totalSessions: 0, totalMinutes: 0,
                    peakConcurrent: 0, unitsRequired: 0, totalCost: 0,
                    effectiveUnitPrice: 0, listPrice: 0, numCalls: 0, avgCallLengthSecs: 0,
                    numSpeechRequests: 0, lexSpeechCost: 0,
                    connectTotalCost: 0,
                    totalCostWithoutConnect: 0, totalCostWithConnect: 0,
                    effectiveSpeechPrice: 0, effectiveConnectPrice: 0,
                    tokensRequired: 0, freeTokens: 0, billableTokens: 0, licenseType: 'named',
                    normalizedSessions: 0, numInteractions: 0, avgMsgs: 10,
                    scenarioResults: [],
                  }}
                  isReady={solutionIsReady(id)}
                  numScenarios={results[id]?.scenarioResults?.length ?? 0}
                />
              ))}
            </div>

            <ScenarioBreakdownTable
              selectedIds={selectedIds}
              scenarios={scenarios}
              results={results}
              solutionIsReady={solutionIsReady}
            />
          </section>

          {isVolumeReady && peakConcurrent > 0 && (
            <section className="peak-section">
              <div className="peak-card">
                <div className="peak-icon">⚡</div>
                <div>
                  <div className="peak-title">Peak Concurrent Capacity</div>
                  <div className="peak-value">{Number(peakConcurrent).toLocaleString()} simultaneous calls</div>
                  <div className="peak-note">
                    Ensure your telephony infrastructure (e.g. SBC ports, CUBE sessions) supports this peak load
                    in addition to the AI Agent Unit licensing above.
                  </div>
                </div>
              </div>
            </section>
          )}

          <footer className="app-footer">
            <p>
              Prices shown are <strong>list price estimates</strong> unless a customer price is entered.
              Contact your Cisco partner or account team for discounted pricing.
            </p>
          </footer>
        </main>}

        {calcTab === 'roi' && (
          <main className="app-main">
            <section className="inputs-section">
              <div className="section-header-static">
                <h2>ROI Break-Even Calculator</h2>
                <p className="section-desc">Compare your current monthly costs against AI investment to find when the solution pays for itself.</p>
              </div>
              <div className="inputs-grid" style={{ marginBottom: '20px' }}>
                <div className="input-field">
                  <label>Current Monthly Cost of Doing Business</label>
                  <span className="input-hint">What you spend per month today (before AI)</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiCurrentCost}
                      min={0}
                      step={1}
                      placeholder="e.g. 50000"
                      onChange={(e) => setRoiCurrentCost(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>AI Professional Services (one-time)</label>
                  <span className="input-hint">Upfront PS cost paid in Month 1</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiPS}
                      min={0}
                      step={1}
                      placeholder="e.g. 75000"
                      onChange={(e) => setRoiPS(e.target.value)}
                    />
                  </div>
                </div>
                <div className="input-field">
                  <label>Expected AI Monthly Usage</label>
                  <span className="input-hint">Recurring monthly AI licensing &amp; usage cost (baseline estimate)</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiMonthlyUsage}
                      min={0}
                      step={1}
                      placeholder="e.g. 10000"
                      onChange={(e) => setRoiMonthlyUsage(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Conservative Monthly Usage <span className="roi-optional-badge">optional</span></label>
                  <span className="input-hint">Higher monthly cost scenario — slower break-even</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiMonthlyConservative}
                      min={0}
                      step={1}
                      placeholder="e.g. 15000"
                      onChange={(e) => setRoiMonthlyConservative(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Aggressive Monthly Usage <span className="roi-optional-badge">optional</span></label>
                  <span className="input-hint">Lower monthly cost scenario — faster break-even</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiMonthlyAggressive}
                      min={0}
                      step={1}
                      placeholder="e.g. 6000"
                      onChange={(e) => setRoiMonthlyAggressive(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Timeline (months)</label>
                  <span className="input-hint">Projection length — defaults to 36 if blank or 0</span>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      value={roiTimeline}
                      min={1}
                      max={120}
                      step={1}
                      placeholder="36"
                      onChange={(e) => setRoiTimeline(e.target.value)}
                    />
                    <span className="input-adornment suffix">mo</span>
                  </div>
                </div>
              </div>

              {roiRows.length > 0 ? (() => {
                const hasConservative = roiRows.some(r => r.conservative !== null)
                const hasAggressive = roiRows.some(r => r.aggressive !== null)
                const anyBreakEven = roiRows.some(r =>
                  r.expected.netSavings >= 0 ||
                  (r.conservative && r.conservative.netSavings >= 0) ||
                  (r.aggressive && r.aggressive.netSavings >= 0)
                )
                const fmtUSD = (v) => `${v >= 0 ? '' : '-'}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                const fmtSavings = (v) => `${v >= 0 ? '+' : ''}${fmtUSD(v)}`
                return (
                  <div className="roi-table-wrap">
                    {!anyBreakEven && (
                      <div className="roi-no-breakeven">
                        No break-even reached within {roiRows.length} months at these inputs.
                      </div>
                    )}
                    <RoiLineChart rows={roiRows} />
                    <table className="roi-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Current</th>
                          {hasConservative && <th className="roi-th-conservative">Cons. Cost</th>}
                          {hasConservative && <th className="roi-th-conservative">Cons. Savings</th>}
                          <th className="roi-th-expected">Expected Cost</th>
                          <th className="roi-th-expected">Expected Savings</th>
                          {hasAggressive && <th className="roi-th-aggressive">Aggr. Cost</th>}
                          {hasAggressive && <th className="roi-th-aggressive">Aggr. Savings</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {roiRows.map((row) => {
                          const anyPositive = row.expected.netSavings >= 0 || (row.conservative?.netSavings >= 0) || (row.aggressive?.netSavings >= 0)
                          const rowClass = row.conservative?.isBreakEven ? 'roi-row-be-conservative'
                            : row.expected.isBreakEven ? 'roi-row-be-expected'
                            : row.aggressive?.isBreakEven ? 'roi-row-be-aggressive'
                            : anyPositive ? 'roi-row-positive' : 'roi-row-negative'
                          return (
                            <tr key={row.month} className={rowClass}>
                              <td className="roi-month-cell">{row.month}</td>
                              <td>{fmtUSD(row.cumulativeCurrent)}</td>
                              {hasConservative && <td>{row.conservative ? fmtUSD(row.conservative.cumulativeAI) : '—'}</td>}
                              {hasConservative && <td className={row.conservative ? (row.conservative.netSavings >= 0 ? 'roi-savings-positive' : 'roi-savings-negative') : ''}>
                                {row.conservative ? fmtSavings(row.conservative.netSavings) : '—'}
                              </td>}
                              <td>{fmtUSD(row.expected.cumulativeAI)}</td>
                              <td className={row.expected.netSavings >= 0 ? 'roi-savings-positive' : 'roi-savings-negative'}>
                                {fmtSavings(row.expected.netSavings)}
                              </td>
                              {hasAggressive && <td>{row.aggressive ? fmtUSD(row.aggressive.cumulativeAI) : '—'}</td>}
                              {hasAggressive && <td className={row.aggressive ? (row.aggressive.netSavings >= 0 ? 'roi-savings-positive' : 'roi-savings-negative') : ''}>
                                {row.aggressive ? fmtSavings(row.aggressive.netSavings) : '—'}
                              </td>}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              })() : (
                <div className="placeholder-msg" style={{ padding: '20px 0' }}>
                  Enter your current monthly cost to generate the break-even table.
                </div>
              )}
            </section>
          </main>
        )}
      </div>
      <Footer platformColor="purple" />
    </Box>
  )
}

export default AiCalculatorApp;
